import * as React from 'react';
import Dialog from '@mui/material/Dialog';
import {DialogTitle, TextField, Box, Typography, MenuItem} from '@mui/material';
import {Translate} from '../../../../../../shared/LanguageService';
import DialogContent from '@mui/material/DialogContent';
import Button from '@mui/material/Button';
import Select from '@mui/material/Select';
import {
    getContentProviderNameFromId,
    getSupportedContentProvidersForManualConnection
} from '../../../../../../shared/ContentProvider';
import {BrokerSystemModalHelpSection} from './BrokerSystemModalHelpSection';
import {useBrokerSystemModal} from './useBrokerSystemModal';

interface IProps {
    show: boolean;
    onHide: () => void;
    brokerFirmContentProvider?: string;
    brokerFirmContentProviderId?: number;
    orderId: number;
    onSaveSuccess: () => Promise<any>;
    onSaveResult: (success: boolean) => void;
}

export const BrokerSystemModal = (props: IProps) => {
    const {
        selectedBrokerSystem,
        setSelectedBrokerSystem,
        objectId,
        setObjectId,
        isLoading,
        activeBrokerSystemId,
        showHelpSection,
        isSaveDisabled,
        hasInvalidObjectIdFormat,
        objectIdValidationMessage,
        handleSave,
    } = useBrokerSystemModal({
        orderId: props.orderId,
        brokerFirmContentProviderId: props.brokerFirmContentProviderId,
        onHide: props.onHide,
        onSaveSuccess: props.onSaveSuccess,
        onSaveResult: props.onSaveResult,
    });

    const hasBrokerSystem = !!props.brokerFirmContentProvider;

    return (
        <Dialog open={props.show} onClose={props.onHide}>
            <DialogTitle sx={{fontWeight: 700}}>
                {hasBrokerSystem
                    ? `${Translate('view-order/basic-order-info/modal/connect-broker-system-title')}${props.brokerFirmContentProvider}`
                    : Translate('view-order/basic-order-info/modal/connect-no-broker-system-title')
                }
            </DialogTitle>
            <DialogContent>
                {hasBrokerSystem ? (
                    <Typography>
                        {Translate('view-order/basic-order-info/modal/connect-broker-system-instructions', {
                            brokerFirmContentProvider: props.brokerFirmContentProvider
                        })}
                    </Typography>
                ) : (
                    <>
                        <Typography mb={2}>
                            {Translate('view-order/basic-order-info/modal/no-broker-system-title')}
                        </Typography>
                        <Select
                            fullWidth
                            value={selectedBrokerSystem ? selectedBrokerSystem : ''}
                            onChange={e => setSelectedBrokerSystem(e.target.value as number)}
                            displayEmpty
                        >
                            <MenuItem value="">
                                {Translate('view-order/basic-order-info/modal/select-broker-system')}
                            </MenuItem>
                            {getSupportedContentProvidersForManualConnection()
                            .filter(provider => provider.id !== 0)
                            .map(provider => (
                                <MenuItem key={provider.id} value={provider.id}>
                                    {getContentProviderNameFromId(provider.id)}
                                </MenuItem>
                            ))}
                        </Select>
                    </>
                )}

                <Box display="flex" gap={2} alignItems="center" mt={2} mb={2}>
                    <TextField
                        autoFocus
                        margin="dense"
                        type="text"
                        label={Translate('view-order/basic-order-info/modal/fill-in-id')}
                        disabled={!hasBrokerSystem && !selectedBrokerSystem}
                        onChange={e => setObjectId(e.target.value)}
                        value={objectId}
                        error={hasInvalidObjectIdFormat}
                        helperText={objectIdValidationMessage}
                    />
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        type="submit"
                        disabled={isSaveDisabled || isLoading}
                    >
                        {Translate('view-order/basic-order-info/modal/save-connection')}
                    </Button>
                </Box>
                {showHelpSection && (
                    <BrokerSystemModalHelpSection
                        brokerSystem={getContentProviderNameFromId(activeBrokerSystemId)}
                        brokerSystemId={activeBrokerSystemId}
                        isLoading={isLoading}
                    />
                )}
            </DialogContent>
        </Dialog>
    );
};
